/**
 * HeaderClock — the running check-in timer, pinned in the TOP BAR so it stays
 * visible everywhere while clocked in. Live-subscribes to TODAY's time entry;
 * renders nothing when not checked in (or the tool/tracking is off — the
 * parent gates mounting). Green pulse while working, blue while on a break;
 * click → /time.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { COLLECTIONS, TIME_ENTRY_ID, minutesToHhMm, type TimeEntry } from '@daaily/shared';
import { db } from '../lib/firebase';
import { clockState, todayStr, workedMinutesLive } from './lib';
import { clockViaSession } from './pendingClock';
import { Pause, Play } from 'lucide-react';
import { Icon } from '../design/Icon';

export function HeaderClock({ email }: { email: string }) {
  const [entry, setEntry] = useState<TimeEntry | undefined>(undefined);
  const [now, setNow] = useState(Date.now());
  const today = todayStr();

  useEffect(() => {
    const ref = doc(db, COLLECTIONS.TIME_ENTRIES, TIME_ENTRY_ID(email.toLowerCase(), today));
    return onSnapshot(ref, (snap) => setEntry(snap.exists() ? (snap.data() as TimeEntry) : undefined));
  }, [email, today]);

  const cs = clockState(entry);
  useEffect(() => {
    if (!cs.checkedIn) return;
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, [cs.checkedIn]);

  const [busy, setBusy] = useState(false);
  if (!cs.checkedIn) return null;
  const worked = workedMinutesLive(entry, now);
  const toggleBreak = () => {
    setBusy(true);
    void clockViaSession({
      date: today,
      action: cs.onBreak ? 'end_break' : 'start_break',
      tzOffsetMinutes: new Date().getTimezoneOffset(),
    }).catch(() => { /* the live snapshot re-syncs state; errors surface on /time */ })
      .finally(() => setBusy(false));
  };

  return (
    <span className={`inline-flex items-center rounded-md text-xs tabular-nums ring-1 transition-colors ${
      cs.onBreak ? 'ring-info/50 text-info' : 'ring-success/50 text-success'
    }`}>
      <Link
        to="/time"
        title={cs.onBreak ? 'On break — open Time Tracking' : 'Checked in — open Time Tracking'}
        className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 hover:bg-surface-2 rounded-l-md"
      >
        <span className={`w-1.5 h-1.5 rounded-full ${cs.onBreak ? 'bg-info' : 'bg-success animate-pulse'}`} />
        {minutesToHhMm(worked)}
        {cs.onBreak ? ' · break' : ''}
      </Link>
      {/* Pause / resume without leaving the page. */}
      <button
        type="button"
        onClick={toggleBreak}
        disabled={busy}
        title={cs.onBreak ? 'End break' : 'Start break'}
        className="px-1.5 py-1 border-l border-line hover:bg-surface-2 rounded-r-md disabled:opacity-50"
      >
        {cs.onBreak ? <Icon as={Play} size={14} fill="currentColor" /> : <Icon as={Pause} size={14} fill="currentColor" />}
      </button>
    </span>
  );
}
